import { createClient } from "@supabase/supabase-js";
import { getStartingEquipment, addStartingEquipment } from "./inventoryService";

const supabase = createClient(
  process.env.REACT_APP_SUPABASE_URL,
  process.env.REACT_APP_SUPABASE_ANON_KEY
);

const getCharacters = async (discordUserId) => {
  // ⚡ OPTIMIZATION: Exclude image_url to reduce egress - images are large!
  const { data: characters, error: charactersError } = await supabase
    .from("characters")
    .select(`
      id, name, class, level, race, background, alignment,
      experience_points, max_hit_points, current_hit_points, temporary_hit_points,
      armor_class, speed, initiative, discord_user_id, created_at, updated_at,
      active, subclass, wand_info,
      base_ability_scores, metamagic_choices, ability_scores, additional_feats,
      additional_asi, asi_choices, casting_style, casting_style_choices,
      current_hit_dice, feat_choices, heritage_choices, hit_points,
      initiative_ability, innate_heritage, innate_heritage_skills,
      level1_choice_type, magic_modifiers, notes, skill_proficiencies,
      skill_expertise, temp_hp, tool_proficiencies, standard_feats,
      subclass_choices, wand_type, ac
    `)
    .eq("discord_user_id", discordUserId)
    .eq("active", true)
    .order("created_at", { ascending: false });

  if (charactersError) {
    throw new Error(`Failed to fetch characters: ${charactersError.message}`);
  }

  if (!characters || characters.length === 0) {
    return [];
  }

  const characterIds = characters.map((char) => char.id);
  const { data: characterResources, error: resourcesError } = await supabase
    .from("character_resources")
    .select("*")
    .in("character_id", characterIds);

  if (resourcesError) {
    console.warn("Failed to load character resources:", resourcesError);
  }

  const resourcesMap = {};
  if (characterResources) {
    characterResources.forEach((resource) => {
      resourcesMap[resource.character_id] = resource;
    });
  }

  const transformedData = characters.map((character) => {
    const resources = resourcesMap[character.id] || {};
    return {
      ...character,
      inspiration: resources.inspiration ?? false,
      sorceryPoints: resources.sorcery_points || 0,
      spellSlots1: resources.spell_slots_1 || 0,
      spellSlots2: resources.spell_slots_2 || 0,
      spellSlots3: resources.spell_slots_3 || 0,
      spellSlots4: resources.spell_slots_4 || 0,
      spellSlots5: resources.spell_slots_5 || 0,
      spellSlots6: resources.spell_slots_6 || 0,
      spellSlots7: resources.spell_slots_7 || 0,
      spellSlots8: resources.spell_slots_8 || 0,
      spellSlots9: resources.spell_slots_9 || 0,
      maxSpellSlots1: resources.max_spell_slots_1 || 0,
      maxSpellSlots2: resources.max_spell_slots_2 || 0,
      maxSpellSlots3: resources.max_spell_slots_3 || 0,
      maxSpellSlots4: resources.max_spell_slots_4 || 0,
      maxSpellSlots5: resources.max_spell_slots_5 || 0,
      maxSpellSlots6: resources.max_spell_slots_6 || 0,
      maxSpellSlots7: resources.max_spell_slots_7 || 0,
      maxSpellSlots8: resources.max_spell_slots_8 || 0,
      maxSpellSlots9: resources.max_spell_slots_9 || 0,
    };
  });

  return transformedData;
};

const getAllCharacters = async () => {
  try {
    // ⚡ OPTIMIZATION: Exclude image_url to reduce egress - images are large!
    const { data, error } = await supabase
      .from("characters")
      .select(
        `
        id, name, class, level, race, background, alignment,
        experience_points, max_hit_points, current_hit_points, temporary_hit_points,
        armor_class, speed, initiative, discord_user_id, created_at, updated_at,
        active, subclass, wand_info,
        base_ability_scores, metamagic_choices, ability_scores, additional_feats,
        additional_asi, asi_choices, casting_style, casting_style_choices,
        current_hit_dice, feat_choices, heritage_choices, hit_points,
        initiative_ability, innate_heritage, innate_heritage_skills,
        level1_choice_type, magic_modifiers, notes, skill_proficiencies,
        skill_expertise, temp_hp, tool_proficiencies, standard_feats,
        subclass_choices, wand_type, ac, archived_at, restored_at,
        discord_users (username, display_name),
        character_resources (
          inspiration,
          sorcery_points,
          spell_slots_1,
          spell_slots_2,
          spell_slots_3,
          spell_slots_4,
          spell_slots_5,
          spell_slots_6,
          spell_slots_7,
          spell_slots_8,
          spell_slots_9,
          max_spell_slots_1,
          max_spell_slots_2,
          max_spell_slots_3,
          max_spell_slots_4,
          max_spell_slots_5,
          max_spell_slots_6,
          max_spell_slots_7,
          max_spell_slots_8,
          max_spell_slots_9
        )
      `
      )
      .eq("active", true)
      .order("created_at", { ascending: false });

    if (!error) {
      const transformedData = (data || []).map((character) => {
        const resources = character.character_resources?.[0] || {};

        return {
          ...character,
          character_resources: undefined,
          inspiration: resources.inspiration ?? false,
          sorceryPoints: resources.sorcery_points || 0,
          spellSlots1: resources.spell_slots_1 || 0,
          spellSlots2: resources.spell_slots_2 || 0,
          spellSlots3: resources.spell_slots_3 || 0,
          spellSlots4: resources.spell_slots_4 || 0,
          spellSlots5: resources.spell_slots_5 || 0,
          spellSlots6: resources.spell_slots_6 || 0,
          spellSlots7: resources.spell_slots_7 || 0,
          spellSlots8: resources.spell_slots_8 || 0,
          spellSlots9: resources.spell_slots_9 || 0,
          maxSpellSlots1: resources.max_spell_slots_1 || 0,
          maxSpellSlots2: resources.max_spell_slots_2 || 0,
          maxSpellSlots3: resources.max_spell_slots_3 || 0,
          maxSpellSlots4: resources.max_spell_slots_4 || 0,
          maxSpellSlots5: resources.max_spell_slots_5 || 0,
          maxSpellSlots6: resources.max_spell_slots_6 || 0,
          maxSpellSlots7: resources.max_spell_slots_7 || 0,
          maxSpellSlots8: resources.max_spell_slots_8 || 0,
          maxSpellSlots9: resources.max_spell_slots_9 || 0,
        };
      });

      return transformedData;
    } else {
      console.warn(
        "❌ JOIN query failed, trying manual approach:",
        error.message
      );
    }
  } catch (joinError) {
    console.warn(
      "🔄 JOIN query failed, trying manual join...",
      joinError.message
    );
  }

  try {
    // ⚡ OPTIMIZATION: Exclude image_url to reduce egress
    const { data: characters, error: charactersError } = await supabase
      .from("characters")
      .select(`
        id, name, class, level, race, background, alignment,
        experience_points, max_hit_points, current_hit_points, temporary_hit_points,
        armor_class, speed, initiative, discord_user_id, created_at, updated_at,
        active, subclass, wand_info,
        base_ability_scores, metamagic_choices, ability_scores, additional_feats,
        additional_asi, asi_choices, casting_style, casting_style_choices,
        current_hit_dice, feat_choices, heritage_choices, hit_points,
        initiative_ability, innate_heritage, innate_heritage_skills,
        level1_choice_type, magic_modifiers, notes, skill_proficiencies,
        skill_expertise, temp_hp, tool_proficiencies, standard_feats,
        subclass_choices, wand_type, ac, archived_at, restored_at
      `)
      .eq("active", true)
      .order("created_at", { ascending: false });

    if (charactersError) {
      throw new Error(`Failed to fetch characters: ${charactersError.message}`);
    }

    if (!characters || characters.length === 0) {
      return [];
    }

    const characterIds = characters.map((char) => char.id);
    const { data: characterResources } = await supabase
      .from("character_resources")
      .select("*")
      .in("character_id", characterIds);

    const resourcesMap = {};
    if (characterResources) {
      characterResources.forEach((resource) => {
        resourcesMap[resource.character_id] = resource;
      });
    }

    const discordUserIds = [
      ...new Set(
        characters.map((char) => char.discord_user_id).filter(Boolean)
      ),
    ];
    let userMap = {};

    if (discordUserIds.length > 0) {
      const { data: discordUsers, error: usersError } = await supabase
        .from("discord_users")
        .select("discord_user_id, username, display_name")
        .in("discord_user_id", discordUserIds);

      if (usersError) {
        console.warn("⚠️ Failed to load discord users:", usersError.message);
      } else if (discordUsers) {
        discordUsers.forEach((user) => {
          userMap[user.discord_user_id] = user;
        });
      }
    }

    const charactersWithUsers = characters.map((character) => {
      const resources = resourcesMap[character.id] || {};

      return {
        ...character,
        discord_users: userMap[character.discord_user_id] || null,
        inspiration: resources.inspiration || 0,
        sorceryPoints: resources.sorcery_points || 0,
        spellSlots1: resources.spell_slots_1 || 0,
        spellSlots2: resources.spell_slots_2 || 0,
        spellSlots3: resources.spell_slots_3 || 0,
        spellSlots4: resources.spell_slots_4 || 0,
        spellSlots5: resources.spell_slots_5 || 0,
        spellSlots6: resources.spell_slots_6 || 0,
        spellSlots7: resources.spell_slots_7 || 0,
        spellSlots8: resources.spell_slots_8 || 0,
        spellSlots9: resources.spell_slots_9 || 0,
        maxSpellSlots1: resources.max_spell_slots_1 || 0,
        maxSpellSlots2: resources.max_spell_slots_2 || 0,
        maxSpellSlots3: resources.max_spell_slots_3 || 0,
        maxSpellSlots4: resources.max_spell_slots_4 || 0,
        maxSpellSlots5: resources.max_spell_slots_5 || 0,
        maxSpellSlots6: resources.max_spell_slots_6 || 0,
        maxSpellSlots7: resources.max_spell_slots_7 || 0,
        maxSpellSlots8: resources.max_spell_slots_8 || 0,
        maxSpellSlots9: resources.max_spell_slots_9 || 0,
      };
    });

    return charactersWithUsers;
  } catch (error) {
    console.error("💥 Error in getAllCharacters:", error);

    try {
      // ⚡ OPTIMIZATION: Exclude image_url to reduce egress
      const { data, error: simpleError } = await supabase
        .from("characters")
        .select(`
          id, name, class, level, race, background, alignment,
          experience_points, max_hit_points, current_hit_points, temporary_hit_points,
          armor_class, speed, initiative, discord_user_id, created_at, updated_at,
          active, subclass, wand_info,
          base_ability_scores, metamagic_choices, ability_scores, additional_feats,
          additional_asi, asi_choices, casting_style, casting_style_choices,
          current_hit_dice, feat_choices, heritage_choices, hit_points,
          initiative_ability, innate_heritage, innate_heritage_skills,
          level1_choice_type, magic_modifiers, notes, skill_proficiencies,
          skill_expertise, temp_hp, tool_proficiencies, standard_feats,
          subclass_choices, wand_type, ac, archived_at, restored_at
        `)
        .eq("active", true)
        .order("created_at", { ascending: false });

      if (simpleError) {
        throw simpleError;
      }

      return (data || []).map((character) => ({
        ...character,
        discord_users: null,
        inspiration: 0,
        sorceryPoints: 0,
      }));
    } catch (lastResortError) {
      console.error(
        "💥 Even simple character loading failed:",
        lastResortError
      );
      throw new Error(`Failed to fetch all characters: ${error.message}`);
    }
  }
};

const isUserForbidden = async (discordUserId) => {
  const { data, error } = await supabase
    .from("user_roles")
    .select("role")
    .eq("discord_user_id", discordUserId)
    .eq("role", "forbidden")
    .maybeSingle();

  if (error && error.code !== "PGRST116") {
    console.error("Error checking forbidden status:", error);
    return false;
  }

  return !!data;
};

const isUserAdmin = async (discordUserId) => {
  try {
    const forbidden = await isUserForbidden(discordUserId);
    if (forbidden) {
      return false;
    }
  } catch (forbiddenError) {
    console.error("❌ Error checking forbidden status:", forbiddenError);
  }

  try {
    const { data, error } = await supabase
      .from("user_roles")
      .select("role, granted_by, granted_at")
      .eq("discord_user_id", discordUserId)
      .eq("role", "admin");

    if (error && error.code !== "PGRST116") {
      console.error("❌ Error checking admin status:", error);
      return false;
    }

    const isAdmin = !!(data && data.length > 0);
    return isAdmin;
  } catch (adminError) {
    console.error("❌ Error in admin check:", adminError);
    return false;
  }
};

const verifyAdminPassword = async (discordUserId, password) => {
  const forbidden = await isUserForbidden(discordUserId);

  if (forbidden) {
    throw new Error(
      "The ancient magic recognizes you as forbidden. Access permanently denied."
    );
  }

  // Check if user is already an admin
  const alreadyAdmin = await isUserAdmin(discordUserId);
  if (alreadyAdmin) {
    console.log("✅ User is already an admin, skipping insert");
    return true;
  }

  const ADMIN_PASSWORD = process.env.REACT_APP_ADMIN_PASSWORD;
  console.log("🔑 Password Comparison Debug:");
  console.log("  Entered password:", password);
  console.log("  Entered password length:", password?.length);
  console.log("  Expected password:", ADMIN_PASSWORD);
  console.log("  Expected password length:", ADMIN_PASSWORD?.length);
  console.log("  Match:", password === ADMIN_PASSWORD);

  if (!ADMIN_PASSWORD) {
    throw new Error("The magical registry is not properly configured");
  }

  if (password !== ADMIN_PASSWORD) {
    throw new Error("The unlocking charm failed");
  }

  const insertData = {
    discord_user_id: discordUserId,
    role: "admin",
    granted_by: discordUserId, // Self-granted via website password
    granted_at: new Date().toISOString(),
  };

  const { error } = await supabase
    .from("user_roles")
    .insert(insertData)
    .select();

  if (error && error.code !== "23505" && error.code !== "42501") {
    console.error("❌ Database error (not a duplicate key or RLS):", error);
    console.error("This is the actual error causing the failure!");
    throw new Error(`Database error: ${error.message}`);
  }

  return true;
};

const getUserRoleStatus = async (discordUserId) => {
  const { data, error } = await supabase
    .from("user_role_status")
    .select("*")
    .eq("discord_user_id", discordUserId)
    .single();

  if (error && error.code !== "PGRST116") {
    console.error("Error checking user role status:", error);
    return { effective_role: "user", roles: ["user"] };
  }

  return data || { effective_role: "user", roles: ["user"] };
};

const setUserRole = async (targetDiscordUserId, role, grantedBy) => {
  const validRoles = ["admin", "moderator", "user", "forbidden"];
  if (!validRoles.includes(role)) {
    throw new Error("Invalid role specified");
  }

  try {
    const { error } = await supabase.from("user_roles").insert({
      discord_user_id: targetDiscordUserId,
      role: role,
      granted_by: grantedBy,
      granted_at: new Date().toISOString(),
    });

    if (error && error.code !== "23505") {
      throw new Error(`Failed to grant ${role} role: ${error.message}`);
    }

    return true;
  } catch (error) {
    console.error("Error setting user role:", error);
    throw error;
  }
};

const removeUserRole = async (targetDiscordUserId, role) => {
  const { error } = await supabase
    .from("user_roles")
    .delete()
    .eq("discord_user_id", targetDiscordUserId)
    .eq("role", role);

  if (error) {
    throw new Error(`Failed to remove ${role} role: ${error.message}`);
  }

  return true;
};

const saveCharacter = async (characterData, discordUserId) => {
  try {
    console.log(
      "📤 characterService.saveCharacter - INSERT payload:",
      JSON.stringify(
        {
          ability_scores: characterData.ability_scores,
          additional_feats: characterData.additional_feats,
          additional_asi: characterData.additional_asi,
          asi_choices: characterData.asi_choices,
          background: characterData.background,
          casting_style: characterData.casting_style,
          casting_style_choices: characterData.casting_style_choices || {},
          current_hit_dice: characterData.level,
          current_hit_points: characterData.hit_points,
          discord_user_id: discordUserId,
          feat_choices: characterData.feat_choices || {},
          heritage_choices: characterData.heritage_choices || {},
          hit_points: characterData.hit_points,
          initiative_ability: characterData.initiative_ability,
          initiative: characterData.initiative || {
            modifier: 0,
            override: null,
          },
          innate_heritage: characterData.innate_heritage,
          innate_heritage_skills: characterData.innate_heritage_skills,
          level: characterData.level,
          level1_choice_type: characterData.level1_choice_type,
          magic_modifiers: characterData.magic_modifiers,
          name: characterData.name,
          notes: characterData.notes || null,
          skill_proficiencies: characterData.skill_proficiencies,
          skill_expertise: characterData.skill_expertise,
          temp_hp: characterData.temp_hp || 0,
          tool_proficiencies: characterData.tool_proficiencies,
          standard_feats: characterData.standard_feats,
          subclass: characterData.subclass,
          subclass_choices: characterData.subclass_choices,
          wand_type: characterData.wand_type,
          wand_info: characterData.wand_info,
          image_url: characterData.image_url || characterData.imageUrl || null,
          base_ability_scores: characterData.base_ability_scores,
          metamagic_choices: characterData.metamagic_choices,
        },
        null,
        2
      )
    );

    const { data: savedCharacter, error: characterError } = await supabase
      .from("characters")
      .insert({
        ability_scores: characterData.ability_scores,
        additional_feats: characterData.additional_feats,
        additional_asi: characterData.additional_asi,
        asi_choices: characterData.asi_choices,
        background: characterData.background,
        casting_style: characterData.casting_style,
        casting_style_choices: characterData.casting_style_choices || {},
        class: characterData.class,
        current_hit_dice: characterData.level,
        current_hit_points: characterData.hit_points,
        discord_user_id: discordUserId,
        feat_choices: characterData.feat_choices || {},
        heritage_choices: characterData.heritage_choices || {},
        hit_points: characterData.hit_points,
        initiative_ability: characterData.initiative_ability,
        initiative: characterData.initiative || { modifier: 0, override: null },
        innate_heritage: characterData.innate_heritage,
        innate_heritage_skills: characterData.innate_heritage_skills,
        level: characterData.level,
        level1_choice_type: characterData.level1_choice_type,
        magic_modifiers: characterData.magic_modifiers,
        name: characterData.name,
        notes: characterData.notes || null,
        skill_proficiencies: characterData.skill_proficiencies,
        skill_expertise: characterData.skill_expertise,
        temp_hp: characterData.temp_hp || 0,
        tool_proficiencies: characterData.tool_proficiencies,
        standard_feats: characterData.standard_feats,
        subclass: characterData.subclass,
        subclass_choices: characterData.subclass_choices,
        wand_type: characterData.wand_type,
        wand_info: characterData.wand_info,
        image_url: characterData.image_url || characterData.imageUrl || null,
        base_ability_scores: characterData.base_ability_scores,
        metamagic_choices: characterData.metamagic_choices,
      })
      .select()
      .single();

    console.log(
      "📥 characterService.saveCharacter - DB response:",
      JSON.stringify(savedCharacter, null, 2)
    );

    if (characterError) {
      console.error(
        "❌ characterService.saveCharacter - error:",
        characterError
      );
      throw characterError;
    }

    if (characterData.background && savedCharacter.id) {
      try {
        const startingEquipment = getStartingEquipment(
          characterData.background
        );

        if (startingEquipment.length > 0) {
          await addStartingEquipment(
            discordUserId,
            savedCharacter.id,
            startingEquipment,
            supabase
          );
        }
      } catch (equipmentError) {
        console.error("Failed to add starting equipment:", equipmentError);
      }
    }

    return savedCharacter;
  } catch (error) {
    console.error("Error in saveCharacter:", error);
    throw error;
  }
};

const updateCharacter = async (characterId, characterData, discordUserId) => {
  try {
    const updatePayload = {
      ability_scores: characterData.ability_scores,
      ac: characterData.ac,
      additional_feats: characterData.additional_feats,
      additional_asi: characterData.additional_asi,
      asi_choices: characterData.asi_choices,
      background: characterData.background,
      casting_style: characterData.casting_style,
      casting_style_choices: characterData.casting_style_choices || {},
      class: characterData.class,
      current_hit_dice: characterData.level,
      current_hit_points: characterData.hit_points,
      feat_choices: characterData.feat_choices || {},
      heritage_choices: characterData.heritage_choices || {},
      hit_points: characterData.hit_points,
      image_url: characterData.image_url || characterData.imageUrl || null,
      initiative_ability: characterData.initiative_ability,
      initiative: characterData.initiative || { modifier: 0, override: null },
      innate_heritage_skills: characterData.innate_heritage_skills,
      innate_heritage: characterData.innate_heritage,
      level: characterData.level,
      level1_choice_type: characterData.level1_choice_type,
      magic_modifiers: characterData.magic_modifiers,
      name: characterData.name,
      notes: characterData.notes,
      skill_expertise: characterData.skill_expertise,
      skill_proficiencies: characterData.skill_proficiencies,
      temp_hp: characterData.temp_hp || 0,
      tool_proficiencies: characterData.tool_proficiencies,
      standard_feats: characterData.standard_feats,
      subclass_choices: characterData.subclass_choices,
      subclass: characterData.subclass,
      updated_at: new Date().toISOString(),
      wand_type: characterData.wand_type,
      wand_info: characterData.wand_info,
      base_ability_scores: characterData.base_ability_scores,
      metamagic_choices: characterData.metamagic_choices,
    };

    console.log(
      "📤 characterService.updateCharacter - UPDATE payload for character ID:",
      characterId
    );
    console.log("UPDATE payload:", JSON.stringify(updatePayload, null, 2));

    const { data: updatedCharacter, error: characterError } = await supabase
      .from("characters")
      .update(updatePayload)
      .eq("id", characterId)
      .eq("discord_user_id", discordUserId)
      .select()
      .single();

    console.log(
      "📥 characterService.updateCharacter - DB response:",
      JSON.stringify(updatedCharacter, null, 2)
    );

    if (characterError) {
      console.error(
        "❌ characterService.updateCharacter - database error:",
        characterError
      );
      throw characterError;
    }

    return updatedCharacter;
  } catch (error) {
    console.error("Error in updateCharacter:", error);
    throw error;
  }
};

const updateCharacterAsAdmin = async (characterId, characterData) => {
  try {
    const { data: updatedCharacter, error: characterError } = await supabase
      .from("characters")
      .update({
        ability_scores: characterData.ability_scores,
        ac: characterData.ac,
        additional_feats: characterData.additional_feats,
        additional_asi: characterData.additional_asi,
        asi_choices: characterData.asi_choices,
        background: characterData.background,
        casting_style: characterData.casting_style,
        casting_style_choices: characterData.casting_style_choices || {},
        class: characterData.class,
        current_hit_dice: characterData.level,
        current_hit_points: characterData.hit_points,
        heritage_choices: characterData.heritage_choices || {},
        hit_points: characterData.hit_points,
        image_url: characterData.image_url || characterData.imageUrl || null,
        initiative_ability: characterData.initiative_ability,
        initiative: characterData.initiative || { modifier: 0, override: null },
        innate_heritage_skills: characterData.innate_heritage_skills,
        innate_heritage: characterData.innate_heritage,
        level: characterData.level,
        level1_choice_type: characterData.level1_choice_type,
        magic_modifiers: characterData.magic_modifiers,
        name: characterData.name,
        skill_expertise: characterData.skill_expertise,
        skill_proficiencies: characterData.skill_proficiencies,
        temp_hp: characterData.temp_hp || 0,
        standard_feats: characterData.standard_feats,
        subclass_choices: characterData.subclass_choices,
        subclass: characterData.subclass,
        updated_at: new Date().toISOString(),
        wand_type: characterData.wand_type,
        wand_info: characterData.wand_info,
        base_ability_scores: characterData.base_ability_scores,
        discord_user_id: characterData.discord_user_id,
        metamagic_choices: characterData.metamagic_choices,
      })
      .eq("id", characterId)
      .eq("active", true)
      .select()
      .single();

    if (characterError) {
      console.error(
        "characterService.updateCharacterAsAdmin - database error:",
        characterError
      );
      throw characterError;
    }

    return updatedCharacter;
  } catch (error) {
    console.error("Error in updateCharacterAsAdmin:", error);
    throw error;
  }
};

const updateCharacterSubclass = async (
  characterId,
  subclass,
  subclassChoices,
  discordUserId
) => {
  if (subclassChoices && typeof subclassChoices !== "object") {
    throw new Error("Invalid subclass choices format - must be an object");
  }

  const { data, error } = await supabase
    .from("characters")
    .update({
      subclass: subclass,
      subclass_choices: subclassChoices || {},
      updated_at: new Date().toISOString(),
    })
    .eq("id", characterId)
    .eq("discord_user_id", discordUserId)
    .eq("active", true)
    .select();

  if (error) {
    throw new Error(`Failed to update character subclass: ${error.message}`);
  }

  return data[0];
};

const getCharacterById = async (characterId, discordUserId) => {
  const { data, error } = await supabase
    .from("characters")
    .select("*")
    .eq("id", characterId)
    .eq("discord_user_id", discordUserId)
    .eq("active", true)
    .single();

  if (error) {
    if (error.code === "PGRST116") {
      throw new Error("Character not found");
    }
    throw new Error(`Failed to fetch character: ${error.message}`);
  }

  return data;
};

const getCharacterByIdAdmin = async (characterId) => {
  const { data, error } = await supabase
    .from("characters")
    .select(
      `
      *,
      discord_users (
        username,
        display_name,
        discord_user_id
      )
    `
    )
    .eq("id", characterId)
    .eq("active", true)

    .single();

  if (error) {
    if (error.code === "PGRST116") {
      throw new Error("Character not found");
    }
    throw new Error(`Failed to fetch character: ${error.message}`);
  }

  return data;
};

const deleteCharacter = async (characterId, discordUserId) => {
  const { data, error } = await supabase
    .from("characters")
    .update({
      active: false,
      archived_at: new Date().toISOString(),
    })
    .eq("id", characterId)
    .eq("discord_user_id", discordUserId)
    .eq("active", true)
    .select();

  if (error) {
    throw new Error(`Failed to archive character: ${error.message}`);
  }

  return data[0];
};

const deleteCharacterAsAdmin = async (characterId) => {
  const { data, error } = await supabase
    .from("characters")
    .update({
      active: false,
      archived_at: new Date().toISOString(),
    })
    .eq("id", characterId)
    .eq("active", true)
    .select();

  if (error) {
    throw new Error(`Failed to archive character: ${error.message}`);
  }

  return data[0];
};

const restoreCharacter = async (characterId, discordUserId) => {
  const { data, error } = await supabase
    .from("characters")
    .update({
      active: true,
      archived_at: null,
      restored_at: new Date().toISOString(),
    })
    .eq("id", characterId)
    .eq("discord_user_id", discordUserId)
    .eq("active", false)
    .select();

  if (error) {
    throw new Error(`Failed to restore character: ${error.message}`);
  }

  return data[0];
};

const getArchivedCharacters = async (discordUserId = null) => {
  // ⚡ OPTIMIZATION: Exclude image_url to reduce egress - images are large!
  let query = supabase
    .from("characters")
    .select(`
      id, name, class, level, race, background, alignment,
      experience_points, max_hit_points, current_hit_points, temporary_hit_points,
      armor_class, speed, initiative, discord_user_id, created_at, updated_at,
      active, subclass, wand_info,
      base_ability_scores, metamagic_choices, ability_scores, additional_feats,
      additional_asi, asi_choices, casting_style, casting_style_choices,
      current_hit_dice, feat_choices, heritage_choices, hit_points,
      initiative_ability, innate_heritage, innate_heritage_skills,
      level1_choice_type, magic_modifiers, notes, skill_proficiencies,
      skill_expertise, temp_hp, tool_proficiencies, standard_feats,
      subclass_choices, wand_type, ac, archived_at, restored_at
    `)
    .eq("active", false)
    .order("archived_at", { ascending: false });

  if (discordUserId) {
    query = query.eq("discord_user_id", discordUserId);
  }

  const { data, error } = await query;

  if (error) {
    throw new Error(`Failed to fetch archived characters: ${error.message}`);
  }

  return data || [];
};

export const characterService = {
  getCharacters,
  getAllCharacters,
  isUserAdmin,
  isUserForbidden,
  verifyAdminPassword,
  getUserRoleStatus,
  setUserRole,
  removeUserRole,
  getCharacterById,
  getCharacterByIdAdmin,
  saveCharacter,
  updateCharacter,
  updateCharacterAsAdmin,
  updateCharacterSubclass,
  deleteCharacter,
  deleteCharacterAsAdmin,
  restoreCharacter,
  getArchivedCharacters,
};
